import { useEffect, useMemo } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@connectrpc/connect-query'
import { create } from '@bufbuild/protobuf'
import { z } from 'zod'
import { ArrowLeft, ArrowUpRight, Inbox } from 'lucide-react'

import { useBreadcrumbFromRoute } from '@/hooks/use-breadcrumb-from-route'
import { useHasJobsFeature } from '@/hooks/use-has-feature'
import { useInquiry } from '@/stores/leads.store'
import { useAttachments } from '@/stores/attachments.store'
import { useContactQuoteInteractionsByQuoteIds } from '@/stores/quotes.store'
import { getJob } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import { GetJobRequestSchema } from '@/gen/cortado/v1/jobs_pb'
import {
  InquiryRejectReasonEnum,
  InquiryStageEnum,
  JobPhaseEnum,
  ParentTypeEnum,
} from '@/gen/cortado/v1/settings_pb'
import { formatEnumName } from '@/lib/enum-utils'
import { formatTimestampWithTime } from '@/lib/date-utils'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Empty, EmptyDescription, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { ActivityTabs } from '@/components/shared/activity-tabs'
import { LeadInquiryInfo } from '@/components/leads/id/lead-inquiry-info'
import { PipelineLeadStageControl } from '@/components/pipeline/pipeline-lead-stage-control'
import { JobAssignee } from '@/components/pipeline/job-assignee'
import { JobRail } from '@/components/pipeline/job-rail'
import { JobContactCard } from '@/components/pipeline/job-contact-card'
import { LeadLastContact } from '@/components/leads/lead-mail-badge'
import { jobChildBreadcrumbs } from '@/components/pipeline/pipeline-breadcrumbs'
import { RejectReasonBanner } from '@/components/pipeline/reject-reason-banner'

const UNTITLED_JOB_TITLE = 'Untitled job'
const NO_IDS: string[] = []

// `$id_` opts out of nesting under the job page route: this is its own page,
// not an outlet inside the job detail.
export const Route = createFileRoute('/_app/_home/pipeline/$id_/leads/$leadId')({
  component: PipelineLeadRouteFromParams,
  validateSearch: z.object({
    tab: z.enum(['conversations']).optional(),
  }),
})

function PipelineLeadRouteFromParams() {
  const { id, leadId } = Route.useParams()
  const { tab } = Route.useSearch()
  return <PipelineLeadRoute jobId={id} leadId={leadId} initialTab={tab} />
}

export function PipelineLeadRoute({
  jobId,
  leadId,
  initialTab,
}: {
  jobId: string
  leadId: string
  initialTab?: 'conversations'
}) {
  const navigate = useNavigate()
  const { hasFeature, hasLoaded } = useHasJobsFeature()

  useEffect(() => {
    // Without the Pipeline, the lead still has its standalone page.
    if (hasLoaded && !hasFeature) {
      navigate({ to: '/leads/$id', params: { id: leadId }, search: initialTab ? { tab: initialTab } : {}, replace: true })
    }
  }, [hasFeature, hasLoaded, initialTab, leadId, navigate])

  if (!hasLoaded || !hasFeature) {
    return <div className="flex min-h-[50dvh] items-center justify-center">Loading...</div>
  }

  return <PipelineLeadPage jobId={jobId} leadId={leadId} initialTab={initialTab} />
}

function NotFound({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <div className="flex flex-1 flex-col rounded-lg border border-dashed py-6 md:py-0">
        <Empty>
          <EmptyMedia variant="icon">
            <Inbox className="h-6 w-6" />
          </EmptyMedia>
          <EmptyTitle>{title}</EmptyTitle>
          <EmptyDescription>{description}</EmptyDescription>
        </Empty>
      </div>
    </div>
  )
}

/**
 * The lead a pipeline job came from: everything the customer submitted
 * (editable, with tags and source), plus the lead's files, conversations,
 * quote views, portal visits and workflow runs. The job's contact, tasks and
 * notes stay in the rail, as on the job's other pages.
 */
export function PipelineLeadPage({
  jobId,
  leadId,
  initialTab,
}: {
  jobId: string
  leadId: string
  initialTab?: 'conversations'
}) {
  const jobRequest = useMemo(() => create(GetJobRequestSchema, { jobId }), [jobId])
  const jobQuery = useQuery(getJob, jobRequest, { enabled: Boolean(jobId) })
  const job = jobQuery.data?.job
  const breadcrumbs = useMemo(() => (job ? jobChildBreadcrumbs(job, 'Lead') : undefined), [job])
  useBreadcrumbFromRoute(breadcrumbs)
  const { inquiry, isLoading: isInquiryLoading, isError: isInquiryError } = useInquiry(leadId)
  const { attachments } = useAttachments({
    parentType: ParentTypeEnum.PARENT_TYPE_INQUIRY,
    parentId: leadId,
  })
  const {
    interactions: quoteInteractions,
    totalCount: quoteInteractionCount,
    isLoading: quoteInteractionsLoading,
  } = useContactQuoteInteractionsByQuoteIds({
    contactId: inquiry?.contact?.id,
    quoteIds: inquiry?.quoteIds ?? NO_IDS,
    limitPerQuote: 100,
  })

  if (jobQuery.isLoading || isInquiryLoading) {
    return (
      <div className="flex h-full flex-col gap-4 p-4">
        <div className="space-y-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-48" />
        </div>
        <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
          <Skeleton className="h-96 w-full rounded-xl" />
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      </div>
    )
  }

  if (jobQuery.isError || !job) {
    return <NotFound title="Job not found" description="We couldn't load this job. It may have been removed." />
  }
  // A lead id from another job (a stale or hand-edited URL) is not this job's.
  if (isInquiryError || !inquiry || job.inquiryId !== inquiry.id) {
    return <NotFound title="Lead not found" description="This lead isn't part of this job." />
  }

  const contact = inquiry.contact
  const heading = [contact?.firstName, contact?.lastName].filter(Boolean).join(' ') || 'Lead'
  const received = formatTimestampWithTime(inquiry.createdAt)
  const isRejected =
    inquiry.stage === InquiryStageEnum.INQUIRY_STAGE_REJECTED &&
    inquiry.rejectReason != null &&
    inquiry.rejectReason !== InquiryRejectReasonEnum.INQUIRY_REJECT_REASON_UNKNOWN

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto p-4">
      <div className="flex flex-wrap items-stretch justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h1 className="text-2xl font-semibold tracking-tight">{heading}</h1>
            {inquiry.archivedAt && <Badge variant="secondary">Archived</Badge>}
          </div>
          {received && <div className="mt-1.5 text-xs/normal text-muted-foreground">Received {received}</div>}
          <LeadLastContact
            className="mt-1.5"
            tags={inquiry.tags}
            contactFirstName={contact?.firstName}
            lastInboundAt={inquiry.lastInboundAt}
            lastOutboundAt={inquiry.lastOutboundAt}
            lastMessageDirection={inquiry.lastMessageDirection}
          />
          <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs/normal text-muted-foreground">
            <Link to="/pipeline/$id" params={{ id: job.id }} className="inline-flex items-center gap-1 hover:text-foreground">
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to {job.title || UNTITLED_JOB_TITLE}
            </Link>
            <Link to="/leads/$id" params={{ id: inquiry.id }} className="inline-flex items-center gap-1 hover:text-foreground">
              Full lead page
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
        <div className="flex flex-col items-end justify-between gap-2">
          {/* The stage is picked here only while the job is a new lead, the same
              rule as the job page's Lead step; later the job's phase drives it. */}
          {job.phase === JobPhaseEnum.JOB_PHASE_NEW ? (
            <PipelineLeadStageControl inquiryId={inquiry.id} className="w-44" />
          ) : (
            <Badge variant="outline">{formatEnumName(inquiry.stage, InquiryStageEnum)}</Badge>
          )}
          <JobAssignee job={job} />
        </div>
      </div>

      {isRejected && <RejectReasonBanner inquiry={inquiry} />}

      <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
        <div className="flex min-w-0 flex-col gap-4">
          <LeadInquiryInfo inquiry={inquiry} sheetLayout="pipeline" />
          {/* Notes and tasks live in the rail's job cards, which include the lead's own. */}
          <ActivityTabs
            title="Lead activity"
            description="Files, conversations, quote views and workflows"
            organizationId={inquiry.organizationId}
            parentType={ParentTypeEnum.PARENT_TYPE_INQUIRY}
            parentId={inquiry.id}
            hideNotesAndTasks
            attachments={attachments}
            quoteInteractions={quoteInteractions}
            quoteInteractionCount={quoteInteractionCount}
            quoteInteractionsLoading={quoteInteractionsLoading}
            showQuoteInteractions
            customerEmail={contact?.email}
            emailContactId={contact?.id}
            showWorkflowRuns
            workflowSourceResourceId={inquiry.id}
            workflowTriggerEntityType="INQUIRY"
            initialTab={initialTab}
          />
        </div>

        <div className="flex min-w-0 flex-col gap-4">
          <JobContactCard job={job} />
          <JobRail job={job} stage="lead" />
        </div>
      </div>
    </div>
  )
}
